import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  TextField,
  Chip,
  Box,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import {
  useAllUsersForUserManagementLazyQuery,
  useAllRolesQuery,
  useAllUserGroupsForUserManagementQuery,
  User,
  UserStatusEnum,
  usePreRegisteredUsersLazyQuery,
  PreRegisteredUsersQuery,
} from '@/__generated__/graphql';
import { emailRegExp } from '@/utils/regex';
import { InviteUsersState } from '@/types';
import { inviteScrollDesign } from '../../styles';
import { abyssColors } from '@/theme/colors';
import { UserAvatarCell } from '../../shared/UserAvatarCell';

type InviteUsersProps = {
  inviteUsersData: InviteUsersState;
  isBlurred: boolean;
  setIsBlurred: React.Dispatch<React.SetStateAction<boolean>>;
  setInviteUsersData: React.Dispatch<React.SetStateAction<InviteUsersState>>;
  tabValue: number;
};

type ExistingUsersInfoProps = {
  userId: string;
  email: string;
  type: 'Invited' | 'Pre-Registered';
  name?: string;
  picture?: string;
  status?: UserStatusEnum;
};

type AutoCompleteProperty = {
  name: string;
  value: string;
};

const preSelectedRoleNames = new Set(['client']);

const ExistingUsersInfo = ({
  userId,
  email,
  type,
  name,
  picture,
  status,
}: ExistingUsersInfoProps) => {
  return (
    <Stack
      key={userId}
      direction="row"
      alignItems="center"
      spacing={1}
      justifyContent="space-between"
    >
      <Stack direction="row" alignItems="center" spacing={1} flex={1}>
        <Box sx={{ width: '25px' }}>
          <UserAvatarCell picture={picture ?? undefined} />
        </Box>
        <Stack direction="column">
          <Typography variant="body2" fontWeight="bold" fontSize="12px">
            {name ?? email}
          </Typography>
          <Typography variant="body2" fontSize="12px">
            {email}
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ pr: 1 }}>
        {type === 'Invited' && (
          <>
            {status === UserStatusEnum.Active && (
              <Typography variant="body2" fontSize="12px" color={abyssColors.success[600]}>
                Active
              </Typography>
            )}
            {status === UserStatusEnum.Pending && (
              <Typography variant="body2" fontSize="12px" color={abyssColors.primary[400]}>
                {type}
              </Typography>
            )}
            {status === UserStatusEnum.Blocked && (
              <Typography variant="body2" fontSize="12px" color={abyssColors.error[600]}>
                Blocked
              </Typography>
            )}
          </>
        )}
        {type === 'Pre-Registered' && (
          <Typography variant="body2" fontSize="12px" color={abyssColors.primary[400]}>
            {type}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export const InviteInputs = ({
  inviteUsersData: { emails, groups, roles },
  isBlurred,
  setInviteUsersData,
  setIsBlurred,
  tabValue,
}: InviteUsersProps) => {
  const [currentEmail, setCurrentEmail] = useState<string>('');

  const { data: userGroupsData } = useAllUserGroupsForUserManagementQuery();
  const { data: userRolesData } = useAllRolesQuery();

  const [fetchSearchedInvitedUsers] = useAllUsersForUserManagementLazyQuery();
  const [searchedInvitedUsers, setSearchedInvitedUsers] = useState<User[]>();
  const [searchedInvitedUserExists, setSearchedInvitedUserExists] = useState<boolean>(false);

  const [fetchSearchedPreRegisteredUsers] = usePreRegisteredUsersLazyQuery();
  const [searchedPreRegisteredUsers, setSearchedPreRegisteredUsers] =
    useState<PreRegisteredUsersQuery['preRegisteredUsers']>();
  const [activeUsers, setActiveUsers] = useState<User[]>();
  const [searchedPreRegisteredUsersExists, setSearchedPreRegisteredUsersExists] =
    useState<boolean>(false);
  const [activeUsersExists, setActiveUsersExists] = useState<boolean>(false);

  const userRoles = useMemo(() => {
    return (
      userRolesData?.allRoles?.reduce((currentRoles: AutoCompleteProperty[], role) => {
        if (role?.name && role?.auth0Id) {
          currentRoles.push({ name: role.name, value: role.auth0Id });
        }
        return currentRoles;
      }, []) || []
    );
  }, [userRolesData]);

  const userGroups = useMemo(
    () => userGroupsData?.allUserGroups?.map(({ name, id }) => ({ name, value: id })) || [],
    [userGroupsData]
  );

  useEffect(() => {
    setSearchedInvitedUserExists(currentEmail !== '' && (searchedInvitedUsers?.length ?? 0) > 0);
  }, [currentEmail, searchedInvitedUsers?.length]);

  useEffect(() => {
    setSearchedPreRegisteredUsersExists(
      currentEmail !== '' && (searchedPreRegisteredUsers?.length ?? 0) > 0
    );
  }, [currentEmail, searchedPreRegisteredUsers?.length]);

  useEffect(() => {
    setActiveUsersExists(currentEmail !== '' && (activeUsers?.length ?? 0) > 0);
  }, [currentEmail, activeUsers?.length]);

  const searchedInvitedUsersEmails = useMemo(() => {
    return searchedInvitedUsers?.map((user) => user.email.toLowerCase()) ?? [];
  }, [searchedInvitedUsers]);

  const searchedPreRegisteredUsersEmails = useMemo(() => {
    return searchedPreRegisteredUsers?.map((user) => user.email.toLowerCase()) ?? [];
  }, [searchedPreRegisteredUsers]);

  const activeUsersEmails = useMemo(() => {
    return activeUsers?.map((user) => user.email.toLowerCase()) ?? [];
  }, [activeUsers]);

  const handleInputChange = useCallback(
    (newValue: AutoCompleteProperty[], type: string) => {
      setInviteUsersData((previousState) => ({
        ...previousState,
        [type]: newValue.map((role) => role.value),
      }));
    },
    [setInviteUsersData]
  );

  const handleEmails = useCallback(
    (newEmails: string[]) => {
      const validEmails = newEmails.filter((email) => emailRegExp.test(email));
      setInviteUsersData((previous) => ({ ...previous, emails: validEmails }));
    },
    [setInviteUsersData]
  );

  const getSearchedInvitedUsers = useCallback(() => {
    fetchSearchedInvitedUsers({
      variables: {
        input: { q: currentEmail },
      },
    }).then(({ data }) => {
      setSearchedInvitedUsers(data?.allUsers);
    });
  }, [currentEmail, fetchSearchedInvitedUsers]);

  const getSearchedPreRegisteredUsers = useCallback(() => {
    fetchSearchedPreRegisteredUsers({
      variables: {
        input: { searchTerm: currentEmail },
      },
    }).then(({ data }) => {
      setSearchedPreRegisteredUsers(data?.preRegisteredUsers);
    });

    fetchSearchedInvitedUsers({
      variables: {
        input: { q: currentEmail, status: UserStatusEnum.Active },
      },
    }).then(({ data }) => {
      setActiveUsers(data?.allUsers);
    });
  }, [currentEmail, fetchSearchedInvitedUsers, fetchSearchedPreRegisteredUsers]);

  useEffect(() => {
    if (currentEmail === '') {
      setSearchedInvitedUserExists(false);
      setSearchedPreRegisteredUsersExists(false);
      setActiveUsersExists(false);
      setSearchedInvitedUsers([]);
      setActiveUsers([]);
      setSearchedPreRegisteredUsers([]);
    }

    if (currentEmail) {
      if (tabValue === 0) {
        getSearchedInvitedUsers();
      }
      if (tabValue === 1) {
        getSearchedPreRegisteredUsers();
      }
    }
  }, [
    currentEmail,
    fetchSearchedInvitedUsers,
    fetchSearchedPreRegisteredUsers,
    getSearchedInvitedUsers,
    getSearchedPreRegisteredUsers,
    tabValue,
  ]);

  const handleEmailKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (['Tab', 'Enter'].includes(event.key)) {
        if (tabValue === 0 && searchedInvitedUsersEmails?.includes(currentEmail)) {
          setCurrentEmail('');
          setSearchedInvitedUsers([]);
          setSearchedInvitedUserExists(false);
          return;
        }

        if (tabValue === 1 && searchedPreRegisteredUsersEmails?.includes(currentEmail)) {
          setCurrentEmail('');
          setSearchedPreRegisteredUsers([]);
          setSearchedPreRegisteredUsersExists(false);
          return;
        }

        if (tabValue === 1 && activeUsersEmails?.includes(currentEmail)) {
          setCurrentEmail('');
          setActiveUsers([]);
          setActiveUsersExists(false);
          return;
        }

        if (!emails.includes(currentEmail)) handleEmails([...emails, currentEmail]);

        setCurrentEmail('');
      }
    },
    [
      activeUsersEmails,
      currentEmail,
      emails,
      handleEmails,
      searchedInvitedUsersEmails,
      searchedPreRegisteredUsersEmails,
      tabValue,
    ]
  );

  const removeEmail = useCallback(
    (emailToRemove: string) => () => {
      handleEmails(emails.filter((email) => email !== emailToRemove));
    },
    [emails, handleEmails]
  );

  const handleEmailBlur = useCallback(() => {
    if (tabValue === 0 && searchedInvitedUsersEmails?.includes(currentEmail)) {
      setCurrentEmail('');
      setSearchedInvitedUsers([]);
      setSearchedInvitedUserExists(false);
      return;
    }

    if (tabValue === 1 && searchedPreRegisteredUsersEmails?.includes(currentEmail)) {
      setCurrentEmail('');
      setSearchedPreRegisteredUsers([]);
      setSearchedPreRegisteredUsersExists(false);
      return;
    }

    if (tabValue === 1 && activeUsersEmails?.includes(currentEmail)) {
      setCurrentEmail('');
      setActiveUsers([]);
      setActiveUsersExists(false);
      return;
    }

    if (!emails.includes(currentEmail)) handleEmails([...emails, currentEmail]);

    setCurrentEmail('');
    setIsBlurred(true);
  }, [
    activeUsersEmails,
    currentEmail,
    emails,
    handleEmails,
    searchedInvitedUsersEmails,
    searchedPreRegisteredUsersEmails,
    setIsBlurred,
    tabValue,
  ]);

  useEffect(() => {
    const filteredRoles = userRolesData?.allRoles?.filter(
      (roleOption) => roleOption && preSelectedRoleNames.has(roleOption?.name ?? '')
    );
    if (filteredRoles) {
      const preSelectedRoles = filteredRoles.map((role) => ({
        name: role?.name ?? '',
        value: role?.auth0Id ?? '',
      }));
      handleInputChange(preSelectedRoles, 'roles');
    }
  }, [handleInputChange, userRolesData?.allRoles]);

  const helperTextMessage = useMemo(() => {
    if (tabValue === 0 && searchedInvitedUsersEmails?.includes(currentEmail)) {
      const status = searchedInvitedUsers?.find((user) => user.email === currentEmail)?.status;
      return status === UserStatusEnum.Active
        ? 'The user is already active'
        : 'The user has already been invited';
    }

    if (tabValue === 1 && searchedPreRegisteredUsersEmails?.includes(currentEmail)) {
      return 'The user has already been pre-registered';
    }

    if (tabValue === 1 && activeUsersEmails?.includes(currentEmail)) {
      return 'The user is already active';
    }

    if (isBlurred && emails.length === 0) {
      return 'Please enter at least one email';
    }
  }, [
    activeUsersEmails,
    currentEmail,
    emails.length,
    isBlurred,
    searchedInvitedUsers,
    searchedInvitedUsersEmails,
    searchedPreRegisteredUsersEmails,
    tabValue,
  ]);

  const isError = useMemo(() => {
    return (
      (isBlurred && emails.length === 0) ||
      (tabValue === 0 && searchedInvitedUsersEmails?.includes(currentEmail)) ||
      (tabValue === 1 && searchedPreRegisteredUsersEmails?.includes(currentEmail)) ||
      (tabValue === 1 && activeUsersEmails?.includes(currentEmail))
    );
  }, [
    activeUsersEmails,
    currentEmail,
    emails.length,
    isBlurred,
    searchedInvitedUsersEmails,
    searchedPreRegisteredUsersEmails,
    tabValue,
  ]);

  return (
    <>
      <TextField
        fullWidth
        label="Emails"
        variant="standard"
        sx={{ mt: 2, mb: 2 }}
        placeholder="Add emails"
        value={currentEmail}
        onChange={(event) => setCurrentEmail(event.target.value.toLowerCase())}
        onKeyDown={handleEmailKeydown}
        onBlur={handleEmailBlur}
        error={isError}
        helperText={helperTextMessage}
      />
      <Box sx={{ maxHeight: '100px', overflow: 'auto', ...inviteScrollDesign }}>
        {emails.map((email) => (
          <Chip
            key={email}
            label={email}
            variant="outlined"
            onDelete={removeEmail(email)}
            sx={{ mr: 1, mb: 1.5 }}
          />
        ))}
      </Box>
      {tabValue === 0 && searchedInvitedUserExists && (
        <Paper elevation={3} sx={{ p: 1, mb: 2, backgroundColor: abyssColors.primary[50] }}>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              maxHeight: 'calc(5 * 40px)',
              overflowY: 'auto',
            }}
          >
            {searchedInvitedUsers?.map((user) => (
              <ExistingUsersInfo
                key={user.id}
                userId={user.id}
                email={user.email}
                name={user.name ?? ''}
                picture={user.picture ?? ''}
                status={user.status ?? UserStatusEnum.Pending}
                type="Invited"
              />
            ))}
          </Stack>
        </Paper>
      )}
      {tabValue === 1 && (activeUsersExists || searchedPreRegisteredUsersExists) && (
        <Paper elevation={3} sx={{ p: 1, mb: 2, backgroundColor: abyssColors.primary[50] }}>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              maxHeight: 'calc(5 * 40px)',
              overflowY: 'auto',
            }}
          >
            {searchedPreRegisteredUsers?.map((preRegisteredUser) => (
              <ExistingUsersInfo
                key={preRegisteredUser.id}
                userId={preRegisteredUser.id}
                email={preRegisteredUser.email}
                type="Pre-Registered"
              />
            ))}

            {activeUsers?.map((activeUser) => (
              <ExistingUsersInfo
                key={activeUser.id}
                userId={activeUser.id}
                email={activeUser.email}
                name={activeUser.name ?? ''}
                picture={activeUser.picture ?? ''}
                status={UserStatusEnum.Active}
                type="Invited"
              />
            ))}
          </Stack>
        </Paper>
      )}
      <Autocomplete
        multiple
        sx={{ mb: 3 }}
        options={userGroups}
        getOptionLabel={(group) => group.name}
        value={userGroups.filter((group) => groups.includes(group.value))}
        onChange={(_, newValue) => handleInputChange(newValue, 'groups')}
        renderInput={(parameters) => (
          <TextField
            variant="standard"
            {...parameters}
            label="Invitee User Group"
            error={isBlurred && groups.length === 0}
            helperText={isBlurred && groups.length === 0 && 'Please select at least one group'}
          />
        )}
        ListboxProps={{ sx: { maxHeight: 180, overflow: 'auto', ...inviteScrollDesign } }}
        ChipProps={{ variant: 'outlined' }}
        renderOption={(props, option, { selected }) => (
          <li {...props} id={`user-group-${option.value}`}>
            <Checkbox checked={selected} />
            {option.name}
          </li>
        )}
      />
      <Autocomplete
        multiple
        sx={{ mb: 3 }}
        options={userRoles}
        getOptionLabel={(role) => role.name}
        value={userRoles.filter((role) => roles.includes(role.value))}
        onChange={(_, newValue) => handleInputChange(newValue, 'roles')}
        renderInput={(parameters) => (
          <TextField variant="standard" {...parameters} label="Invitee User Role" />
        )}
        ListboxProps={{ sx: { maxHeight: 110, overflow: 'auto', ...inviteScrollDesign } }}
        ChipProps={{ variant: 'outlined' }}
        renderOption={(props, option, { selected }) => (
          <li {...props} id={`user-role-${option.value}`}>
            <Checkbox checked={selected} />
            {option.name}
          </li>
        )}
      />
    </>
  );
};
